import type { ClientSSOContext } from './types'

const SSO_KEYS = {
  CONTEXT: 'sso_context',
  STATE_NONCE: 'sso_state_nonce',
  STATE_CLIENT: 'sso_state_client',
  REDIRECT: 'sso_redirect_after_login',
  PENDING_FRESH_LOGIN: 'sso_pending_fresh_login',
} as const

// Pre-rename key that persists in the browsers of already logged-in SSO
// users. Read it as a fallback so those sessions keep their SSO context
// (losing it would degrade client resolution to the ambiguous clientId
// lookup). Remove once all active sessions have rolled over.
const LEGACY_CONTEXT_KEY = 'clientSSOContext'
// Pre-rename redirect key; same migration treatment as the context key.
const LEGACY_REDIRECT_KEY = 'redirectAfterLogin'

// The Cognito logout hop (SSO landing -> /logout -> /login -> SSO landing)
// is a few redirects; anything older than this is a stale leftover.
const PENDING_FRESH_LOGIN_TTL_MS = 2 * 60 * 1000

interface PendingFreshLogin {
  clientName: string
  search: string
  createdAt: number
}

export const ssoStorage = {
  getContext(): ClientSSOContext | null {
    try {
      const raw = localStorage.getItem(SSO_KEYS.CONTEXT) ?? localStorage.getItem(LEGACY_CONTEXT_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  },

  setContext(ctx: ClientSSOContext): void {
    localStorage.setItem(SSO_KEYS.CONTEXT, JSON.stringify(ctx))
    localStorage.removeItem(LEGACY_CONTEXT_KEY)
  },

  mergeContext(partial: Partial<ClientSSOContext>): void {
    const existing = this.getContext()
    this.setContext({ ...(existing ?? {}), ...partial })
  },

  clearContext(): void {
    localStorage.removeItem(SSO_KEYS.CONTEXT)
    localStorage.removeItem(LEGACY_CONTEXT_KEY)
  },

  getOAuthState(): { nonce: string; clientName: string } | null {
    const nonce = localStorage.getItem(SSO_KEYS.STATE_NONCE)
    const clientName = localStorage.getItem(SSO_KEYS.STATE_CLIENT)
    if (!nonce || !clientName) return null
    return { nonce, clientName }
  },

  setStateNonce(nonce: string, clientName: string): void {
    localStorage.setItem(SSO_KEYS.STATE_NONCE, nonce)
    localStorage.setItem(SSO_KEYS.STATE_CLIENT, clientName)
  },

  clearStateNonce(): void {
    localStorage.removeItem(SSO_KEYS.STATE_NONCE)
    localStorage.removeItem(SSO_KEYS.STATE_CLIENT)
  },

  setRedirectUrl(url: string): void {
    localStorage.setItem(SSO_KEYS.REDIRECT, url)
    localStorage.removeItem(LEGACY_REDIRECT_KEY)
  },

  getRedirectUrl(): string | null {
    return localStorage.getItem(SSO_KEYS.REDIRECT) ?? localStorage.getItem(LEGACY_REDIRECT_KEY)
  },

  setPendingFreshLogin(clientName: string, search: string): void {
    const pending: PendingFreshLogin = { clientName, search, createdAt: Date.now() }
    localStorage.setItem(SSO_KEYS.PENDING_FRESH_LOGIN, JSON.stringify(pending))
  },

  getAndClearPendingFreshLogin(): PendingFreshLogin | null {
    try {
      const raw = localStorage.getItem(SSO_KEYS.PENDING_FRESH_LOGIN)
      if (!raw) return null
      localStorage.removeItem(SSO_KEYS.PENDING_FRESH_LOGIN)
      const pending: PendingFreshLogin = JSON.parse(raw)
      if (
        typeof pending.clientName !== 'string' ||
        !pending.clientName ||
        typeof pending.search !== 'string' ||
        typeof pending.createdAt !== 'number' ||
        Date.now() - pending.createdAt > PENDING_FRESH_LOGIN_TTL_MS
      ) {
        return null
      }
      return pending
    } catch {
      return null
    }
  },

  getAndClearRedirectUrl(): string | null {
    const url = this.getRedirectUrl()
    localStorage.removeItem(SSO_KEYS.REDIRECT)
    localStorage.removeItem(LEGACY_REDIRECT_KEY)
    return url
  },
}
